import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import JsSIP from 'jssip';

function formatSipError(prefix: string, payload?: any): string {
  const cause = payload?.cause || payload?.message || '';
  const code = payload?.code || payload?.status_code || '';
  const reason = payload?.reason_phrase || '';
  return [prefix, cause, code ? `code=${code}` : '', reason ? `reason=${reason}` : '']
    .filter(Boolean)
    .join(' | ');
}

export interface CentroSipConnectConfig {
  sipUsername: string;
  sipPassword: string;
  sipDomain: string;
  sipUaWsUrl: string;
  iceServers?: RTCIceServer[];
}

@Injectable({ providedIn: 'root' })
export class CentroSipService {
  private ua: any = null;
  private session: any = null;
  private remoteAudio: HTMLAudioElement | null = null;
  private localStream: MediaStream | null = null;
  private registered = false;
  private connecting: Promise<void> | null = null;
  private ringing$ = new BehaviorSubject<boolean>(false);
  private webrtcReady$ = new BehaviorSubject<boolean>(false);
  private inCall$ = new BehaviorSubject<boolean>(false);
  private muted$ = new BehaviorSubject<boolean>(false);
  private held$ = new BehaviorSubject<boolean>(false);
  private callEnded$ = new Subject<string>();
  private iceDropTimer: ReturnType<typeof setTimeout> | null = null;
  private autoAnswer = true;

  setAutoAnswer(enabled: boolean): void {
    this.autoAnswer = enabled === true;
    console.log('[CentroSIP] autoAnswer', this.autoAnswer);
  }

  isAutoAnswerEnabled(): boolean {
    return this.autoAnswer;
  }
  private answerInFlight = false;
  private iceServers: RTCIceServer[] = [{ urls: 'stun:stun.l.google.com:19302' }];
  private lastConfig: CentroSipConnectConfig | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  readonly isRinging$ = this.ringing$.asObservable();
  readonly isWebrtcReady$ = this.webrtcReady$.asObservable();
  readonly isInCall$ = this.inCall$.asObservable();
  readonly isMuted$ = this.muted$.asObservable();
  readonly isHeld$ = this.held$.asObservable();
  readonly onCallEnded$ = this.callEnded$.asObservable();

  isRegistered(): boolean {
    return this.registered;
  }

  hasActiveSession(): boolean {
    return !!(this.session && !this.session.isEnded?.());
  }

  getRemotePhone(): string {
    const uri = this.session?.remote_identity?.uri;
    const raw = uri?.user || uri?.toString?.() || '';
    return String(raw).replace(/\D/g, '').slice(-10);
  }

  isRinging(): boolean {
    return this.ringing$.value;
  }

  isWebrtcConnected(): boolean {
    return this.webrtcReady$.value;
  }

  isInCall(): boolean {
    return this.inCall$.value;
  }

  isMuted(): boolean {
    return this.muted$.value;
  }

  isHeld(): boolean {
    return this.held$.value;
  }

  async connect(config: CentroSipConnectConfig): Promise<void> {
    if (this.hasActiveSession()) {
      return;
    }
    if (this.registered && this.ua) {
      return;
    }
    if (this.connecting) {
      return this.connecting;
    }
    this.lastConfig = config;
    this.connecting = this.startUa(config).finally(() => {
      this.connecting = null;
    });
    return this.connecting;
  }

  private clearReconnectTimer() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private scheduleReconnect(reason: string) {
    if (!this.lastConfig || this.hasActiveSession() || this.connecting || this.reconnectTimer) {
      return;
    }
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.registered || this.hasActiveSession() || !this.lastConfig) {
        return;
      }
      console.warn('[CentroSIP] reconnecting transport', reason);
      void this.connect(this.lastConfig).catch((err) => {
        console.warn('[CentroSIP] reconnect failed', err?.message || err);
        this.scheduleReconnect('retry');
      });
    }, 3000);
  }

  private markWebrtcConnected(reason: string) {
    if (this.webrtcReady$.value) return;
    this.webrtcReady$.next(true);
    console.log('[CentroSIP] WebRTC connected successfully — calls can proceed', reason);
  }

  private markInCall(active: boolean) {
    if (this.inCall$.value === active) return;
    this.inCall$.next(active);
  }

  private setMuteState(muted: boolean) {
    if (this.muted$.value !== muted) {
      this.muted$.next(muted);
    }
  }

  private setHoldState(held: boolean) {
    if (this.held$.value !== held) {
      this.held$.next(held);
    }
  }

  private bindMedia(session: any, peerConnection?: RTCPeerConnection) {
    const pc: RTCPeerConnection | undefined = peerConnection || session?.connection;
    if (!pc) {
      console.warn('[CentroSIP] no RTCPeerConnection yet');
      return;
    }
    if ((pc as any).__centroBound) {
      return;
    }
    (pc as any).__centroBound = true;
    console.log('[CentroSIP] RTCPeerConnection ready', {
      connectionState: pc.connectionState,
      iceConnectionState: pc.iceConnectionState,
      signalingState: pc.signalingState,
    });
    pc.addEventListener('icecandidate', (ev: RTCPeerConnectionIceEvent) => {
      if (!ev.candidate) {
        console.log('[CentroSIP] ICE gathering complete');
        return;
      }
      console.log('[CentroSIP] ICE candidate', ev.candidate.candidate);
    });
    pc.addEventListener('icecandidateerror', (ev: Event) => {
      const detail = (ev as RTCPeerConnectionIceErrorEvent);
      console.warn('[CentroSIP] ICE candidate error', {
        url: detail?.url || '',
        errorCode: detail?.errorCode,
        errorText: detail?.errorText || ''
      });
    });
    const onState = () => {
      console.log('[CentroSIP] WebRTC state', {
        connectionState: pc.connectionState,
        iceConnectionState: pc.iceConnectionState,
        signalingState: pc.signalingState,
      });
      if (
        pc.connectionState === 'connected' ||
        pc.iceConnectionState === 'connected' ||
        pc.iceConnectionState === 'completed'
      ) {
        this.markWebrtcConnected(`${pc.connectionState}/${pc.iceConnectionState}`);
        this.clearIceDropTimer();
      }
      if (pc.connectionState === 'failed' || pc.iceConnectionState === 'failed') {
        if (this.hasActiveSession()) {
          console.error('[CentroSIP] WebRTC media path failed', {
            connectionState: pc.connectionState,
            iceConnectionState: pc.iceConnectionState,
          });
        }
        this.webrtcReady$.next(false);
        this.scheduleIceDropEnd();
      }
    };
    pc.addEventListener('connectionstatechange', onState);
    pc.addEventListener('iceconnectionstatechange', onState);
    pc.addEventListener('track', (ev: RTCTrackEvent) => {
      const stream = ev.streams?.[0];
      console.log('[CentroSIP] remote audio track received', {
        kind: ev.track?.kind,
        id: ev.track?.id,
        streams: ev.streams?.length || 0,
      });
      if (!stream) return;
      if (!this.remoteAudio) {
        this.remoteAudio = new Audio();
        this.remoteAudio.autoplay = true;
      }
      this.remoteAudio.srcObject = stream;
      this.remoteAudio.muted = this.held$.value;
      void this.remoteAudio.play?.()
        .then(() => {
          console.log('[CentroSIP] remote audio playing');
          this.markWebrtcConnected('remote-audio-playing');
        })
        .catch((err) => console.warn('[CentroSIP] audio play blocked', err?.message || err));
    });
    onState();
  }

  private clearIceDropTimer() {
    if (this.iceDropTimer) {
      clearTimeout(this.iceDropTimer);
      this.iceDropTimer = null;
    }
  }

  private scheduleIceDropEnd() {
    if (this.iceDropTimer || !this.inCall$.value) return;
    this.iceDropTimer = setTimeout(() => {
      this.iceDropTimer = null;
      if (!this.inCall$.value || !this.session) return;
      const pc: RTCPeerConnection | undefined = this.session?.connection;
      const ice = pc?.iceConnectionState || '';
      if (!['failed'].includes(ice)) return;
      console.warn('[CentroSIP] ICE failed — ending call', ice);
      this.terminateActiveCall('ice-drop');
    }, 5000);
  }

  terminateActiveCall(reason = 'terminate'): void {
    const session = this.session;
    if (session && !session.isEnded?.()) {
      console.warn('[CentroSIP] terminateActiveCall', reason);
      try {
        session.terminate();
      } catch (_e) {}
    }
    this.clearSession(session, reason);
  }

  private clearSession(session: any, reason = 'ended') {
    const currentSession = this.session;
    const isCurrentSession = currentSession === session;
    if (!isCurrentSession && currentSession && !currentSession.isEnded?.()) {
      // Ignore teardown from an older SIP leg so the active leg is not dropped from UI state.
      console.log('[CentroSIP] ignoring stale session end event', { reason });
      return;
    }
    this.clearIceDropTimer();
    const wasLive = this.inCall$.value || this.ringing$.value;
    if (isCurrentSession) {
      this.session = null;
    }
    this.answerInFlight = false;
    this.ringing$.next(false);
    this.webrtcReady$.next(false);
    this.setMuteState(false);
    this.setHoldState(false);
    this.markInCall(false);
    if (this.localStream) {
      this.localStream.getTracks().forEach((t) => t.stop());
      this.localStream = null;
    }
    if (this.remoteAudio) {
      this.remoteAudio.muted = false;
      this.remoteAudio.srcObject = null;
    }
    if (wasLive) {
      this.callEnded$.next(reason);
    }
  }

  private canAnswer(session: any): boolean {
    if (!session || session.isEnded?.()) return false;
    const status = session.status;
    if (typeof status === 'number' && status !== 4) {
      return false;
    }
    return true;
  }

  private applySenderMute(muted: boolean): boolean {
    const pc: RTCPeerConnection | undefined = this.session?.connection;
    let touched = false;
    if (pc?.getSenders) {
      pc.getSenders().forEach((sender) => {
        if (sender.track && sender.track.kind === 'audio') {
          sender.track.enabled = !muted;
          touched = true;
        }
      });
    }
    if (this.localStream) {
      this.localStream.getAudioTracks().forEach((t) => {
        t.enabled = !muted;
        touched = true;
      });
    }
    return touched;
  }

  answerIncoming(): boolean {
    const session = this.session;
    if (!session || this.answerInFlight || !this.canAnswer(session)) {
      return false;
    }
    this.answerInFlight = true;
    void this.answerWithMic(session);
    return true;
  }

  private getPcConfig(): RTCConfiguration {
    return {
      iceServers: this.iceServers.length ? this.iceServers : [{ urls: 'stun:stun.l.google.com:19302' }],
      iceTransportPolicy: 'all',
    };
  }

  private async answerWithMic(session: any): Promise<void> {
    try {
      console.log('[CentroSIP] answering incoming call...');
      if (!this.localStream) {
        this.localStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      }
      session.answer({
        mediaConstraints: { audio: true, video: false },
        mediaStream: this.localStream,
        pcConfig: this.getPcConfig(),
      });
      this.bindMedia(session);
    } catch (e: any) {
      this.answerInFlight = false;
      console.error('[CentroSIP] answer failed', e?.message || e);
      try {
        session.terminate({ status_code: 480, reason_phrase: 'Media Failed' });
      } catch (_e) {}
    }
  }

  rejectIncoming(): void {
    if (this.session && !this.session.isEnded?.()) {
      try {
        this.session.terminate();
      } catch (_e) {}
    }
    this.session = null;
    this.answerInFlight = false;
    this.ringing$.next(false);
    this.webrtcReady$.next(false);
    this.setMuteState(false);
    this.setHoldState(false);
    this.markInCall(false);
  }

  private async startUa(config: CentroSipConnectConfig): Promise<void> {
    if (this.hasActiveSession()) {
      return;
    }
    const user = String(config.sipUsername || '').trim();
    const pwd = String(config.sipPassword || '').trim();
    const domain = String(config.sipDomain || '').trim();
    const wsUrl = String(config.sipUaWsUrl || '').trim();
    if (!user || !pwd || !domain || !wsUrl) {
      throw new Error('Centro SIP config incomplete (username, password, domain, ws url)');
    }
    this.iceServers = Array.isArray(config.iceServers) && config.iceServers.length
      ? config.iceServers
      : [{ urls: 'stun:stun.l.google.com:19302' }];

    console.log('[CentroSIP] connect', {
      sipUsername: user,
      sipPassword: pwd ? `${pwd[0]}***(${pwd.length})` : '(empty)',
      sipDomain: domain,
      sipUaWsUrl: wsUrl,
      iceServers: this.iceServers.map((s) => s.urls),
    });

    if (!JsSIP?.WebSocketInterface || !JsSIP?.UA) {
      throw new Error('JsSIP failed to load');
    }

    if (!this.hasActiveSession()) {
      this.teardown(true);
    }
    this.registered = false;
    this.clearReconnectTimer();

    return new Promise<void>((resolve, reject) => {
      const socket = new JsSIP.WebSocketInterface(wsUrl);
      const ua = new JsSIP.UA({
        sockets: [socket],
        uri: `sip:${user}@${domain}`,
        password: pwd,
        register: true,
        session_timers: false,
        connection_recovery_min_interval: 2,
        connection_recovery_max_interval: 20,
        register_expires: 300,
      });

      const fail = (msg: string, payload?: any) => {
        const finalMsg = formatSipError(msg, payload);
        console.error('[CentroSIP]', finalMsg, payload || '');
        this.registered = false;
        reject(new Error(finalMsg));
      };

      ua.on('registered', () => {
        console.log('[CentroSIP] registered', user);
        console.log('[CentroSIP] SIP signaling ready — waiting for calls');
        this.registered = true;
        this.clearReconnectTimer();
        resolve();
      });
      ua.on('registrationFailed', (e: any) => fail('SIP registration failed', e));
      ua.on('connected', () => {
        console.log('[CentroSIP] WebSocket transport connected');
        this.clearReconnectTimer();
      });
      ua.on('disconnected', (e: any) => {
        console.warn('[CentroSIP] transport disconnected', e || '');
        const inCall = this.hasActiveSession() || this.ringing$.value;
        if (inCall) {
          console.warn('[CentroSIP] transport dropped during active call — keeping media session');
          return;
        }
        this.registered = false;
        this.webrtcReady$.next(false);
        this.scheduleReconnect('transport-disconnected');
      });
      ua.on('newRTCSession', (data: any) => {
        if (data.originator !== 'remote' || !data.session) {
          return;
        }
        if (this.session && this.session !== data.session && !this.session.isEnded?.()) {
          // Ensure only one active leg drives UI state; tear down previous dangling leg safely.
          console.warn('[CentroSIP] replacing previous active session with new incoming leg');
          try {
            this.session.terminate();
          } catch (_e) {}
          this.clearSession(this.session, 'replaced-by-new-session');
        }
        console.log('[CentroSIP] incoming call', data.session?.remote_identity?.uri?.toString?.() || '');
        this.session = data.session;
        const session = data.session;
        this.answerInFlight = false;
        this.ringing$.next(true);
        this.webrtcReady$.next(false);
        this.setMuteState(false);
        this.setHoldState(false);
        this.markInCall(false);

        session.on('peerconnection', (e: any) => {
          this.bindMedia(session, e?.peerconnection);
        });
        session.on('ended', () => {
          const pc = session?.connection;
          console.log('[CentroSIP] call ended', {
            iceConnectionState: pc?.iceConnectionState || '',
            connectionState: pc?.connectionState || ''
          });
          this.clearSession(session, 'ended');
        });
        session.on('failed', (e: any) => {
          const pc = session?.connection;
          console.error('[CentroSIP] call failed', {
            cause: e?.cause || e?.message || e,
            originator: e?.originator || '',
            iceConnectionState: pc?.iceConnectionState || '',
            connectionState: pc?.connectionState || ''
          }, e);
          this.clearSession(session, 'failed');
        });
        session.on('accepted', () => {
          console.log('[CentroSIP] call accepted (SIP 200)');
          this.ringing$.next(false);
          this.markInCall(true);
          this.bindMedia(session);
        });
        session.on('confirmed', () => {
          console.log('[CentroSIP] call confirmed (ACK done)');
          this.ringing$.next(false);
          this.markInCall(true);
          this.bindMedia(session);
        });
        session.on('hold', (e: any) => {
          if (e?.originator === 'local' || e?.originator === 'remote') {
            this.setHoldState(true);
            if (this.remoteAudio) this.remoteAudio.muted = true;
            console.log('[CentroSIP] hold', e?.originator);
          }
        });
        session.on('unhold', (e: any) => {
          if (e?.originator === 'local' || e?.originator === 'remote') {
            this.setHoldState(false);
            if (this.remoteAudio) this.remoteAudio.muted = false;
            console.log('[CentroSIP] unhold', e?.originator);
          }
        });
        session.on('muted', (e: any) => {
          if (e?.audio) this.setMuteState(true);
        });
        session.on('unmuted', (e: any) => {
          if (e?.audio) this.setMuteState(false);
        });

        if (this.autoAnswer) {
          setTimeout(() => {
            if (!this.autoAnswer) {
              console.log('[CentroSIP] auto-answer skipped (disabled)');
              return;
            }
            if (this.session === session && this.ringing$.value && !this.answerInFlight) {
              this.answerIncoming();
            }
          }, 250);
        }
      });

      this.ua = ua;
      ua.start();
      setTimeout(() => {
        if (!this.registered) {
          fail('SIP registration timed out');
        }
      }, 20000);
    });
  }

  prepareForOutboundDial(): void {}

  hangupLocal(): void {
    this.rejectIncoming();
    if (this.remoteAudio) {
      this.remoteAudio.srcObject = null;
    }
  }

  setMuted(muted: boolean): boolean {
    if (!this.session || this.session.isEnded?.()) {
      return false;
    }
    try {
      if (muted) {
        this.session.mute({ audio: true, video: false });
      } else {
        this.session.unmute({ audio: true, video: false });
      }
    } catch (_e) {}
    const ok = this.applySenderMute(muted);
    this.setMuteState(muted);
    console.log('[CentroSIP] mute', muted, ok ? 'ok' : 'no-tracks');
    return true;
  }

  toggleMute(): boolean {
    return this.setMuted(!this.muted$.value);
  }

  hold(): boolean {
    const session = this.session;
    if (!session || session.isEnded?.() || this.held$.value) {
      return false;
    }
    if (this.remoteAudio) this.remoteAudio.muted = true;
    this.applySenderMute(true);
    this.setHoldState(true);
    console.log('[CentroSIP] hold (local media)');
    return true;
  }

  unhold(): boolean {
    const session = this.session;
    if (!session || session.isEnded?.() || !this.held$.value) {
      return false;
    }
    if (this.remoteAudio) this.remoteAudio.muted = false;
    if (!this.muted$.value) {
      this.applySenderMute(false);
    }
    this.setHoldState(false);
    console.log('[CentroSIP] unhold (local media)');
    return true;
  }

  teardown(stopUa = true): void {
    this.clearReconnectTimer();
    if (this.hasActiveSession()) {
      this.hangupLocal();
    } else {
      this.rejectIncoming();
    }
    this.registered = false;
    this.webrtcReady$.next(false);
    this.setMuteState(false);
    this.setHoldState(false);
    this.markInCall(false);
    if (stopUa && this.ua) {
      try {
        this.ua.stop();
      } catch (_e) {}
      this.ua = null;
    }
    if (stopUa) {
      this.lastConfig = null;
    }
  }
}
